// src/features/incidents/ImageAnalysisPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Analytics,
  PhotoLibrary,
  TrendingUp,
  Warning,
  Error,
  CheckCircle,
  ArrowBack
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import { getIncident } from './incidentsService';
import ImageAnalysisPanel from './ImageAnalysisPanel';
import imageAnalysisEngine from './incidentsImageAnalysis';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`image-analysis-tabpanel-${index}`}
      aria-labelledby={`image-analysis-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ImageAnalysisPage() {
  const { t } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [engineStats, setEngineStats] = useState(null);

  useEffect(() => {
    loadIncident();
    setEngineStats(imageAnalysisEngine.getStats());
  }, [id]);

  const loadIncident = async () => {
    try {
      setLoading(true);
      const incidentData = await getIncident(id);
      setIncident(incidentData);
    } catch (error) {
      console.error('Error loading incident:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('common_loading') || 'Loading...'}
        </Typography>
      </Box>
    );
  }

  if (!incident) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          {t('incidents_not_found') || 'Incident not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Кнопка назад */}
      <Button 
        startIcon={<ArrowBack />} 
        onClick={() => navigate('/incidents/image-analysis')}
        sx={{ mb: 2 }}
      >
        {t('COMMON_BACK') || 'Back'}
      </Button>

      {/* Заголовок */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            🔍 {t('image_analysis_title') || 'AI Image Analysis'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {incident.type} • {incident.clientName} • {new Date(incident.date).toLocaleDateString()}
          </Typography>
        </Box>

        {/* Статистика моделі */}
        {engineStats && (
          <Stack spacing={1} alignItems="flex-end">
            <Chip
              icon={<Analytics />}
              label={`AI Model v${engineStats.model?.version || '1.0.0'}`}
              variant="outlined"
              color={engineStats.isInitialized ? 'success' : 'warning'}
            />
            <Typography variant="caption" color="text.secondary">
              {engineStats.isInitialized 
                ? (t('image_analysis_model_ready') || 'Model ready')
                : (t('image_analysis_model_loading') || 'Loading model...')
              }
            </Typography>
          </Stack>
        )}
      </Stack>

      {/* Таби */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab 
            icon={<PhotoLibrary />} 
            label={t('image_analysis_photos_list') || 'Photos Analysis'} 
          />
          <Tab 
            icon={<TrendingUp />} 
            label={t('image_analysis_detailed_report') || 'Detailed Report'} 
          />
          <Tab 
            icon={<Analytics />} 
            label={t('image_analysis_export_results') || 'Export Results'} 
          />
        </Tabs>

        {/* Вміст табів */}
        <TabPanel value={tabValue} index={0}>
          <ImageAnalysisPanel 
            incident={incident}
            onAnalysisComplete={loadIncident}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <DetailedAnalysisReport incident={incident} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <ExportAnalysisResults incident={incident} />
        </TabPanel>
      </Paper>
    </Box>
  );
}

// Компонент детального звіту
function DetailedAnalysisReport({ incident }) {
  const { t } = useI18n();

  if (!incident.imageAnalysis) {
    return (
      <Alert severity="info">
        {t('image_analysis_no_data') || 'No analysis data available. Run image analysis first.'}
      </Alert>
    );
  }

  const allAnomalies = incident.imageAnalysis.flatMap(analysis => 
    analysis.analysis.anomalies.map(anomaly => ({
      ...anomaly,
      photoName: analysis.photoName,
      confidence: analysis.analysis.confidence
    }))
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        📊 {t('image_analysis_detailed_report') || 'Detailed Analysis Report'}
      </Typography>
      
      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('image_analysis_total_photos') || 'Total Photos'}
              </Typography>
              <Typography variant="h4">
                {incident.imageAnalysis.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('image_analysis_anomalies') || 'Anomalies'}
              </Typography>
              <Typography variant="h4" color="warning.main">
                {allAnomalies.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('image_analysis_critical') || 'Critical'}
              </Typography>
              <Typography variant="h4" color="error.main">
                {allAnomalies.filter(a => a.severity === 'high').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('image_analysis_confidence') || 'Avg Confidence'}
              </Typography>
              <Typography variant="h4">
                {Math.round(incident.imageAnalysis.reduce((sum, a) => sum + a.analysis.confidence, 0) / incident.imageAnalysis.length * 100)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Таблиця аномалій */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        🚨 {t('image_analysis_detected_anomalies') || 'Detected Anomalies'}
      </Typography>
      
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('image_analysis_anomaly_type') || 'Anomaly Type'}</TableCell>
              <TableCell>{t('image_analysis_severity') || 'Severity'}</TableCell>
              <TableCell align="right">{t('image_analysis_confidence') || 'Confidence'}</TableCell>
              <TableCell>{t('image_analysis_photo') || 'Photo'}</TableCell>
              <TableCell>{t('image_analysis_description') || 'Description'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allAnomalies.map((anomaly, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Chip 
                    label={t(`image_analysis_anomaly_${anomaly.type}`) || anomaly.type}
                    size="small"
                    color={anomaly.severity === 'high' ? 'error' : 'warning'}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {anomaly.severity === 'high' ? <Error color="error" /> : 
                     anomaly.severity === 'medium' ? <Warning color="warning" /> : 
                     <CheckCircle color="success" />}
                    <Typography>
                      {t(`image_analysis_severity_${anomaly.severity}`) || anomaly.severity}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="h6">
                    {Math.round(anomaly.confidence * 100)}%
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {anomaly.photoName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {anomaly.description}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// Компонент експорту результатів
function ExportAnalysisResults({ incident }) {
  const { t } = useI18n();

  const handleExportJSON = () => {
    const data = {
      incidentId: incident.id,
      analysisDate: new Date().toISOString(),
      imageAnalysis: incident.imageAnalysis
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `image_analysis_${incident.id}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    alert('PDF export will be implemented soon');
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        📤 {t('image_analysis_export_results') || 'Export Analysis Results'}
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                JSON Export
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Export analysis results in JSON format for further processing
              </Typography>
              <Button 
                variant="contained" 
                onClick={handleExportJSON}
                disabled={!incident.imageAnalysis}
              >
                Export JSON
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                PDF Report
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Generate a comprehensive PDF report with images and analysis
              </Typography>
              <Button 
                variant="outlined" 
                onClick={handleExportPDF}
                disabled={!incident.imageAnalysis}
              >
                Generate PDF
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {!incident.imageAnalysis && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          {t('image_analysis_no_data') || 'No analysis data available. Run image analysis first.'}
        </Alert>
      )}
    </Box>
  );
}